/**
 * Body text typography components for readable content
 */

import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text, TextProps } from '../Text';
import { useTheme } from '../../themes';

export interface BodyProps extends Omit<TextProps, 'variant'> {
  children: React.ReactNode;
}

export interface ParagraphProps extends BodyProps {
  spacing?: 'none' | 'sm' | 'md' | 'lg';
}

/**
 * Standard body text component
 */
export function Body({ children, ...props }: BodyProps) {
  return (
    <Text variant='body' {...props}>
      {children}
    </Text>
  );
}

/**
 * Large body text for emphasis or introductory content
 */
export function BodyLarge({ children, ...props }: BodyProps) {
  return (
    <Text variant='bodyLarge' {...props}>
      {children}
    </Text>
  );
}

/**
 * Small body text for secondary information
 */
export function BodySmall({ children, ...props }: BodyProps) {
  return (
    <Text variant='bodySmall' {...props}>
      {children}
    </Text>
  );
}

/**
 * Paragraph component with proper spacing
 */
export function Paragraph({
  children,
  spacing = 'md',
  style,
  ...props
}: ParagraphProps) {
  const { theme } = useTheme();

  const spacingValues = {
    none: 0,
    sm: theme.spacing.spacing[2],
    md: theme.spacing.spacing[4],
    lg: theme.spacing.spacing[6],
  };

  const paragraphStyle = [
    styles.paragraph,
    { marginBottom: spacingValues[spacing] },
    style,
  ];

  return (
    <Text variant='body' style={paragraphStyle} {...props}>
      {children}
    </Text>
  );
}

/**
 * Lead paragraph for introductory text
 */
export function Lead({ children, ...props }: BodyProps) {
  return (
    <Text variant='bodyLarge' color='secondary' style={styles.lead} {...props}>
      {children}
    </Text>
  );
}

/**
 * Blockquote component for quoted text
 */
export function Blockquote({ children, style, ...props }: BodyProps) {
  const { theme } = useTheme();

  const blockquoteStyle = [
    styles.blockquote,
    {
      borderLeftColor: theme.colors.border.primary,
      paddingLeft: theme.spacing.spacing[4],
      marginLeft: theme.spacing.spacing[2],
      marginVertical: theme.spacing.spacing[4],
    },
    style,
  ];

  return (
    <View style={blockquoteStyle}>
      <Text
        variant='body'
        color='secondary'
        style={styles.blockquoteText}
        {...props}
      >
        {children}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  paragraph: {
    // Additional paragraph-specific styles can go here
  },
  lead: {
    // Lead text styling
    fontWeight: '400',
  },
  blockquote: {
    borderLeftWidth: 4,
  },
  blockquoteText: {
    fontStyle: 'italic',
  },
});
